/**
 * Ask the platforms for history the routine sync will never reach.
 *
 * Sync fetches the last seven days, so an account connected today shows a week
 * and nothing before it — permanently, because nothing else ever asks for
 * anything older. On one audited account that meant the database held 41% of
 * the Facebook spend that actually happened, with five whole months missing and
 * every figure for that channel understated by more than half.
 *
 * Deliberately NOT a per-account button in the rows above. A backfill is a
 * decision about the workspace and about API budget — tens of requests per
 * account — so it gets one place, one range, and the cost stated before the
 * button is pressed rather than discovered afterwards.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { History, Loader2, Info } from 'lucide-react';
import {
  monthlyChunks,
  defaultBackfillRange,
  type BackfillChunk,
  type BackfillBatchOutcome,
} from '@/services/backfillService';

export interface BackfillAccount {
  id: string;
  platform: string;
  accountName: string;
}

export interface BackfillPanelProps {
  accounts: BackfillAccount[];
  /** False for a viewer: the control renders, disabled, saying why. */
  canEdit: boolean;
  onRequest: (accountIds: string[], range: BackfillChunk) => Promise<BackfillBatchOutcome>;
}

/**
 * Twelve is the default because it is the history a marketing-mix model is
 * conventionally fitted on. Thirty-six is the ceiling because Facebook refuses
 * anything older — error 3018, "the start date of the time range cannot be
 * beyond 37 months from the current date", verified against the live API rather
 * than taken from documentation.
 *
 * The ceiling was described here for months while the longest button offered
 * said 24, and the gap cost a real import: on 2026-10-06 an account's only
 * retrievable Facebook money — 910,314 impressions against 10,000 baht — sat in
 * December 2023, and 140 backfill jobs across three accounts never once asked
 * for it, because 24 months reached only October 2024. Thirty-six reaches it
 * and still lands inside the floor by a month, so the last option a reader can
 * press is now the last one the platform will answer.
 */
const MONTH_OPTIONS = [3, 6, 12, 24, 36] as const;

export const BackfillPanel: React.FC<BackfillPanelProps> = ({ accounts, canEdit, onRequest }) => {
  const { t } = useTranslation();
  const [months, setMonths] = useState<number>(12);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => defaultBackfillRange(months), [months]);
  const perAccount = useMemo(() => monthlyChunks(range.start, range.end).length, [range]);
  const total = perAccount * accounts.length;

  const request = useCallback(async () => {
    if (!canEdit || busy || accounts.length === 0) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { queued, alreadyQueued, failures } = await onRequest(accounts.map((a) => a.id), range);
      // A month that is already waiting is not queued a second time, so a repeat
      // click can send nothing at all. Said apart from what was sent: directly
      // under "Queues N requests", one line for both reads as a second batch of
      // platform requests going out.
      if (alreadyQueued === 0) setResult(t('backfill.queued', { count: queued }));
      else if (queued === 0) setResult(t('backfill.alreadyQueued', { waiting: alreadyQueued }));
      else setResult(t('backfill.queuedSome', { added: queued, waiting: alreadyQueued }));
      // Beside the success, not instead of it: the accounts that went through
      // are genuinely queued, and the one that did not is the thing to act on —
      // so it is named, with its own reason.
      if (failures.length > 0) {
        const nameOf = (id: string): string => accounts.find((a) => a.id === id)?.accountName ?? id;
        const list = failures
          .map((f) => `${nameOf(f.accountId)} — ${f.reason || t('backfill.errQueueFailed')}`)
          .join('; ');
        setError(t('backfill.someFailed', { list }));
      }
    } catch (e) {
      // `||`, not a bare instanceof: an Error with an empty message rendered
      // nothing at all — no result, no alert.
      setError((e instanceof Error && e.message) || t('backfill.errQueueFailed'));
    } finally {
      setBusy(false);
    }
  }, [canEdit, busy, accounts, range, onRequest, t]);

  if (accounts.length === 0) return null;

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-4">
      <h3 className="flex items-center gap-2 text-base font-bold text-ink-900">
        <History className="h-4 w-4 text-neutral-700" aria-hidden="true" />
        {t('backfill.title')}
      </h3>
      <p className="mt-1 text-sm text-neutral-700">{t('backfill.intro')}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {MONTH_OPTIONS.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMonths(m)}
            disabled={!canEdit || busy}
            aria-pressed={m === months}
            className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
              m === months ? 'border-ink-900 bg-neutral-100 font-medium text-ink-900' : 'border-neutral-200 text-neutral-700'
            } ${!canEdit || busy ? 'cursor-not-allowed opacity-60' : 'hover:border-neutral-300'}`}
          >
            {t('backfill.months', { count: m })}
          </button>
        ))}
      </div>

      {/* The cost, before the button rather than after it. A year across four
          accounts is fifty-odd requests to the platforms; that is a decision,
          not a side effect of a click. */}
      <p className="mt-3 text-xs text-neutral-700">
        {t('backfill.cost', {
          jobs: total,
          accounts: accounts.length,
          from: range.start,
          to: range.end,
        })}
      </p>

      <button
        type="button"
        onClick={() => void request()}
        disabled={!canEdit || busy}
        className={`mt-3 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white ${
          !canEdit || busy ? 'cursor-not-allowed bg-neutral-300' : 'bg-ink-950 hover:bg-ink-800'
        }`}
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {busy ? t('backfill.queueing') : t('backfill.action')}
      </button>

      {/* Said before anyone runs it, because it is the one way the result can
          disagree with the platform's own totals and look like a bug. */}
      <p className="mt-3 flex items-start gap-1.5 text-xs text-neutral-700">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('backfill.caveat')}
      </p>

      {!canEdit && <p className="mt-2 text-xs text-neutral-700">{t('backfill.permission')}</p>}
      {result && (
        <p role="status" className="mt-2 text-sm text-success-700">
          {result}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-error-700">
          {error}
        </p>
      )}
    </section>
  );
};

export default BackfillPanel;
